import React, { useCallback, useEffect, useState } from 'react';
import { api, btnPrimary, btnSecondary, card, InlineError, input, labelCls, Skeleton, useToast } from '../ui/Kit';

// Candidate database → Settings (company admins, bundle 4a-2): the rejection e-mail template, how long candidates are
// kept, how long an owner's lock lasts, and the e-mail-in address (shown once a provider is enabled). The hosted careers
// page is hidden — each company will connect its own careers site later (job feed API, embed snippet, apply API).

export default function CandidateSettings() {
  const toast = useToast();
  const [s, setS] = useState(null);
  const [form, setForm] = useState({});
  const [tpl, setTpl] = useState({ subject: '', body: '' });
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const load = useCallback(() => api('/api/candidates/settings')
    .then((x) => {
      setS(x);
      setForm({ candidate_retention_months: x.candidate_retention_months || 24, keep_forever: x.candidate_retention_months == null,
        candidate_ownership_days: x.candidate_ownership_days });
      setTpl({ subject: x.rejection_email.subject, body: x.rejection_email.body });
    }).catch((e) => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const save = async (body, what) => {
    setBusy(what); setError('');
    try { const x = await api('/api/candidates/settings', { method: 'PATCH', body }); setS(x); toast.show('Saved'); }
    catch (e) { setError(e.message); }
    setBusy('');
  };
  const showPreview = async () => {
    try { setPreview(await api('/api/candidates/settings/rejection-preview', { method: 'POST', body: tpl })); setError(''); }
    catch (e) { setError(e.message); }
  };
  if (!s) return <div className={card + ' p-5 space-y-3'}>{error ? <InlineError>{error}</InlineError> : <><Skeleton /><Skeleton /><Skeleton /></>}</div>;
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="space-y-5">
      <InlineError>{error}</InlineError>
      <section className={card + ' p-5'}>
        <h3 className="text-base font-bold">Rejection e-mail</h3>
        <p className="text-sm text-[#64748B] mt-0.5">Sent when a recruiter rejects a candidate and chooses “Send now” or “Send later” (2 business days, your time zone), from the recruiter (replies go to them).
          Merge fields: <code>{'{first_name}'}</code> <code>{'{job_title}'}</code> <code>{'{company}'}</code>.</p>
        <label className="block mt-3"><span className={labelCls}>Subject</span><input className={input} value={tpl.subject} maxLength={200} onChange={(e) => setTpl({ ...tpl, subject: e.target.value })} /></label>
        <label className="block mt-3"><span className={labelCls}>Message</span><textarea className={input} rows={9} maxLength={5000} value={tpl.body} onChange={(e) => setTpl({ ...tpl, body: e.target.value })} data-testid="rejection-body" /></label>
        <div className="flex flex-wrap gap-2 mt-3">
          <button className={btnPrimary} disabled={busy === 'tpl'} onClick={() => save({ rejection_email_subject: tpl.subject, rejection_email_body: tpl.body }, 'tpl')}>Save template</button>
          <button className={btnSecondary} onClick={showPreview}>Preview</button>
          {!s.rejection_email.is_default && <button className={btnSecondary} onClick={() => save({ reset_rejection_email: true }, 'tpl').then(load)}>Use the default</button>}
        </div>
        {preview && (
          <div className="mt-4 border border-[#E6E9F0] rounded-xl p-4 bg-[#F8FAFC]" data-testid="rejection-preview">
            <p className="text-sm font-bold">{preview.subject}</p>
            <p className="text-sm whitespace-pre-line mt-2 text-[#334155]">{preview.body}</p>
          </div>
        )}
      </section>

      <section className={card + ' p-5'}>
        <h3 className="text-base font-bold">Keeping candidate data</h3>
        <div className="grid sm:grid-cols-2 gap-4 mt-3">
          <div>
            <label className="block"><span className={labelCls}>Delete candidates with no activity after (months)</span>
              <input type="number" min={6} max={120} className={input} disabled={form.keep_forever} value={form.candidate_retention_months} onChange={f('candidate_retention_months')} /></label>
            <label className="inline-flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={form.keep_forever} onChange={(e) => setForm({ ...form, keep_forever: e.target.checked })} /> Keep until deleted by hand</label>
            <p className="text-xs text-[#64748B] mt-1">Candidates on an open application are never deleted.</p>
          </div>
          <div>
            <label className="block"><span className={labelCls}>A recruiter’s ownership lasts (days without activity)</span>
              <input type="number" min={7} max={180} className={input} value={form.candidate_ownership_days} onChange={f('candidate_ownership_days')} /></label>
            <p className="text-xs text-[#64748B] mt-1">The first recruiter to log a call or e-mail owns the candidate for that job.</p>
          </div>
        </div>
        <button className={btnSecondary + ' mt-3'} disabled={busy === 'keep'} onClick={() => save(form.keep_forever
          ? { keep_forever: true, candidate_ownership_days: Number(form.candidate_ownership_days) }
          : { candidate_retention_months: Number(form.candidate_retention_months), candidate_ownership_days: Number(form.candidate_ownership_days) }, 'keep')}>Save</button>
      </section>

      <section className={card + ' p-5'} data-testid="reason-codes">
        <h3 className="text-base font-bold">Reasons recruiters can pick</h3>
        <p className="text-sm text-[#64748B] mt-0.5">Hide a reject / pending / withdrawn reason your company doesn’t use. Reasons can’t be renamed — reports stay comparable. “Other” is always offered.</p>
        <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 mt-3">
          {(s.reason_codes || []).map((r) => (
            <label key={r.key} className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!r.hidden} disabled={busy === 'codes'}
              onChange={() => save({ hidden_reason_codes: (s.reason_codes || []).filter((x) => (x.key === r.key ? !x.hidden : x.hidden)).map((x) => x.key) }, 'codes')} />
              <span><span className="text-[#64748B] capitalize">{r.kind}:</span> {r.label}</span></label>))}
        </div>
      </section>

      <section className={card + ' p-5'}>
        <h3 className="text-base font-bold">Resumes by e-mail</h3>
        {s.email_in.enabled ? (s.email_in.address
          ? <p className="text-sm mt-1">Forward resumes to <b>{s.email_in.address}</b>. Put a job’s ID (e.g. TAL-00001-038) in the subject to add them to that job.</p>
          : <button className={btnSecondary + ' mt-2'} onClick={() => save({ email_in_address: true }, 'mail')}>Create our address</button>)
          : <p className="text-sm text-[#64748B] mt-1">Not enabled yet — ask Quali5d support to turn it on for your company.</p>}
      </section>
    </div>
  );
}
